import React, { useState, useEffect } from 'react';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import { Card, Spin, message, Button, Space } from 'antd';
import { ReloadOutlined, CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { deviceSensorService } from '../../../../../services/deviceSensor.service';// Adjust path as needed

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Function to create asset icon with speed
const createAssetIcon = (speed = 0) => {
  const speedText = Math.round(speed).toString();
  const svgContent = `
    <svg width="75" height="87.5" viewBox="0 0 300 350" xmlns="http://www.w3.org/2000/svg">
      <!-- Pin base (AiRagri green) -->
      <path d="M225 137.5 C225 89.2 185.8 50 137.5 50 S50 89.2 50 137.5 c0 48.3 87.5 192.5 87.5 192.5 s87.5-144.2 87.5-192.5 z" fill="#0d3825"/>
      
      <!-- Status ring - Full circle in blue -->
      <circle cx="137.5" cy="137.5" r="58.3" fill="white" stroke="white" stroke-width="14"/>
      
      <!-- Icon background circle (empty/white) -->
      <circle cx="137.5" cy="137.5" r="42" fill="white"/>
      
      <!-- Speed bubble -->
      <circle cx="220" cy="80" r="30" fill="#0d3825"/>
      <text x="220" y="88" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" fill="white" font-weight="bold">${speedText}</text>
    </svg>
  `;
  
  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgContent),
    iconSize: [60, 70],
    iconAnchor: [37.5, 87.5],
    popupAnchor: [0, -87.5]
  });
};

const AssetTrackingLocation = ({ sensorId, deviceId, timezone = 'Australia/Melbourne', fullModal = false }) => {
  const [loading, setLoading] = useState(false);
  const [sensor, setSensor] = useState(null);
  const [lastLocation, setLastLocation] = useState(null);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [mapCenter, setMapCenter] = useState([-37.8136, 144.9631]); // Default to Melbourne

  const dateFormat = 'DD/MM/YYYY HH:mm:ss';

  const fetchSensorData = async (sensorId, deviceId, timezone) => {
    try {
      setLoading(true);
      
      // Use same date range logic as SensorDetail
      const endDate = moment().tz(timezone);
      const startDate = moment().tz(timezone).subtract(23, 'hours').subtract(59, 'minutes');
      
      const params = { 
        // Format dates exactly like SensorDetail
        start_date: startDate.format('DD/MM/YYYY HH:mm:ss'),
        end_date: endDate.format('DD/MM/YYYY HH:mm:ss'),
        sensor_id: sensorId,
        timezone: timezone
      };
      
      console.log('AssetTracking API Params:', params); // Debug log
      
      const response = await deviceSensorService.getDetail(deviceId, params);
      
      console.log('AssetTracking API Response:', response); // Debug log
      
      if (response && response.device) {
        let deviceData;
        if (Array.isArray(response.device)) {
          deviceData = response.device.find(d => d.id === parseInt(deviceId)) || response.device[0];
        } else {
          deviceData = response.device;
        }
        
        if (deviceData && deviceData.sensors && deviceData.sensors.length > 0) {
          const targetSensor = deviceData.sensors.find(s => s.id === parseInt(sensorId));
          
          if (targetSensor) {
            setSensor(targetSensor);
            
            // Find the most recent location from timelines
            if (targetSensor.timelines && targetSensor.timelines.length > 0) {
              let latestLocation = null;
              let latestTimestamp = 0;
              
              targetSensor.timelines.forEach(timeline => {
                const lat = timeline.latitude?.value;
                const lng = timeline.longitude?.value;
                const speed = timeline.speed?.value;
                const time = timeline.latitude?.time || timeline.longitude?.time;
                
                if (lat && lng && lat !== 0 && lng !== 0 && time) {
                  const timestamp = moment(time, 'DD-MM-YYYY HH:mm').valueOf();
                  
                  if (timestamp > latestTimestamp) {
                    latestTimestamp = timestamp;
                    latestLocation = {
                      lat: lat,
                      lng: lng,
                      speed: speed || 0,
                      time: time
                    };
                  }
                }
              });
              
              if (latestLocation) {
                setLastLocation(latestLocation);
                setCurrentSpeed(latestLocation.speed);
                setMapCenter([latestLocation.lat, latestLocation.lng]);
              } else {
                message.info('No valid location data found');
              }
            } else {
              message.info('No timeline data available');
            }
          } else {
            message.warning('Target sensor not found in response');
          }
        } else {
          message.warning('No sensor data found');
        }
      } else {
        message.error('Failed to fetch sensor data - invalid response structure');
      }
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      message.error(`Failed to fetch sensor data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (sensorId && deviceId) {
      fetchSensorData(sensorId, deviceId, timezone);
    }
  };

  useEffect(() => {
    if (sensorId && deviceId) {
      fetchSensorData(sensorId, deviceId, timezone);
    }
  }, [sensorId, deviceId, timezone]);

  if (!sensorId || !deviceId) {
    return (
      <Card 
        title="Asset Tracking Location"
        style={fullModal ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}
        bodyStyle={fullModal ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <CarOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
          <p>Please provide sensor ID and device ID to track asset location.</p>
        </div>
      </Card>
    );
  }

  const cardStyle = fullModal ? {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
  } : {};

  const bodyStyle = fullModal ? {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    overflow: 'hidden'
  } : {};

  return (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EnvironmentOutlined />
          <span>{sensor?.name || 'Loading...'}</span>
        </div>
      }
      style={cardStyle}
      bodyStyle={bodyStyle}
    >
      <Spin spinning={loading} style={fullModal ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}>
        {lastLocation ? (
          <div style={fullModal ? { display: 'flex', flexDirection: 'column', height: '100%' } : {}}>
            <div style={{ 
              marginBottom: fullModal ? '12px' : '16px', 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '6px',
              flexShrink: 0
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space wrap>
                  <span><strong>Device:</strong> {sensor?.terminal_id}</span>
                  <span><strong>Asset:</strong> {sensor?.asset?.title}</span>
                </Space>
                <Space wrap>
                  <span><strong>Last Update:</strong> {lastLocation.time}</span>
                  <span><strong>Speed:</strong> {currentSpeed} km/h</span>
                  <span><strong>Coordinates:</strong> {lastLocation.lat.toFixed(6)}, {lastLocation.lng.toFixed(6)}</span>
                </Space>
              </Space>
            </div>
            
            <div style={{ 
              height: fullModal ? 'calc(100% - 90px)' : '400px', 
              width: '100%',
              flex: fullModal ? 1 : 'none',
              minHeight: fullModal ? '200px' : 'auto'
            }}>
              <Map 
                center={mapCenter} 
                zoom={15} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                <Marker 
                  position={[lastLocation.lat, lastLocation.lng]}
                  icon={createAssetIcon(currentSpeed)}
                >
                  <Popup>
                    <div>
                      <strong>{sensor?.asset?.title || 'Asset'}</strong>
                      <br />
                      <strong>Device:</strong> {sensor?.terminal_id}
                      <br />
                      <strong>Last Seen:</strong> {lastLocation.time}
                      <br />
                      <strong>Speed:</strong> {currentSpeed} km/h
                      <br />
                      <strong>Coordinates:</strong> {lastLocation.lat.toFixed(6)}, {lastLocation.lng.toFixed(6)}
                    </div>
                  </Popup>
                </Marker>
              </Map>
            </div>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            flex: fullModal ? 1 : 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <EnvironmentOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
            <p>No recent location data available.</p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              The asset may not have reported its location in the last 24 hours.
            </p>
            <Button type="primary" onClick={handleRefresh} loading={loading}>
              Try Refreshing
            </Button>
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default AssetTrackingLocation;